import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import { FormInput } from 'components/molecules';
import LabeledStatus from 'components/molecules/labeledStatus/LabeledStatus';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useComponentVisible, useHasPermission } from 'hooks';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useTranslation } from 'react-i18next';
import { PiShoppingBag } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetMerchListInfinite } from 'services/merch';
import { IMerchResponse, IMerchSearch } from 'services/merch/types';
import { STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { getNodes } from 'utils/table/nodes.utils';
import { useSearchById, useSetSearchById } from 'store/state';

enum MERCH_COLUMNS {
  name,
  ean,
  status,
}

const merchSearchInitialValues: IMerchSearch = {
  EAN: '',
  name: '',
  status: undefined,
};

const Merch = () => {
  usePermissionGuard('MerchPermissionsLIST**', '/', true, 'music-product-digital');
  const hasPermission = useHasPermission();

  const columnsVisibility = useTableColumnsVisibility(
    'MerchList1',
    MERCH_COLUMNS
  );

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters = searchById[columnsVisibility.tableId] || merchSearchInitialValues;

  const { data, isLoading, fetchNextPage, isFetchingNextPage } =
    useGetMerchListInfinite(filters);

  const tableData = getNodes<IMerchResponse>(data?.pages.flat());

  const { t } = useTranslation();
  const navigate = useNavigate();
  const i18nextPrefix = 'merch';
  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const onSelectChange = (action: Action, state: State) => {
    if (!hasDetailPermission) {
      return;
    }
    
    navigate(`${state.id ?? state.ids[state.ids.length - 1]}/images`, {
      relative: 'path',
    });
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  const hasDetailPermission = hasPermission('MerchPermissionsLIST**', 128);
  const tableSelect = hasDetailPermission ? select : undefined;

  const { getSelectOptions } = useEnumToSelectOptions();
  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          path=''
          hideBackButton
          topbarContent={
            <>
              <div className='flex'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleComponentVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
                {/* <Table.CustomColumnsButton
                  columns={MERCH_COLUMNS}
                  isClear
                  visibility={columnsVisibility}
                  i18nextPrefix={i18nextPrefix}
                /> */}
              </div>
              {hasPermission('MerchPermissionsLIST**', 1) && (
                <Button
                  variant={BUTTON_VARIANT.submit}
                  onClick={() => navigate('create/images', { replace: false })}>
                  <Button.Text
                    text={t('button.create-what', {
                      what: t(`${i18nextPrefix}.what`),
                    })}
                  />
                  <Button.Icon icon={<PiShoppingBag />} />
                </Button>
              )}
            </>
          }>
          <QueryFilter<IMerchSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<IMerchSearch>
                ref={ref}
                isOpen={isComponentVisible}
                onDebouncedFormChange={values => setSearchById(columnsVisibility.tableId, values)}>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='status'
                    label={t('input.label.status')}
                    options={getSelectOptions(STATUS, 'enum.STATUS')}
                  />
                  <FormInput.Text name='name' label={t('input.label.name')} />
                  <FormInput.Text
                    name='EAN'
                    label={t('input.label.ean')}
                    placeholder='1234567890'
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isComponentVisible}
                customLabel={data =>
                  data.key === 'status'
                    ? t(`enum.STATUS.${STATUS[data.value as STATUS]}`)
                    : `${data.value}`
                }
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        select={tableSelect}
        columns={[
          {
            label: `${t(
              `${i18nextPrefix}.table.${MERCH_COLUMNS[MERCH_COLUMNS.name]}`
            )}`,
            renderCell: item => (
              <TableItem>
                <div className='flex flex-row items-center gap-4'>
                  <TableItem.Image
                    url={item.images?.find(img => img.isMain)?.imagePreviewLink}
                    alt={item.name}
                  />

                  <TableItem.Text text={item.name} />
                </div>
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(MERCH_COLUMNS.name),
          },

          {
            label: t(
              `${i18nextPrefix}.table.${MERCH_COLUMNS[MERCH_COLUMNS.ean]}`
            ),
            renderCell: item => (
              <TableItem>
                <TableItem.Text text={item.ean} />
              </TableItem>
            ),
            hide: columnsVisibility.hide(MERCH_COLUMNS.ean),
            resize: true,
          },
          {
            label: t(
              `${i18nextPrefix}.table.${MERCH_COLUMNS[MERCH_COLUMNS.status]}`
            ),
            renderCell: item => (
              <TableItem justifyPosition='center'>
                <LabeledStatus status={item.merchStatus} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(MERCH_COLUMNS.status),
          },
        ]}
        displayMode
        loading={isLoading || isFetchingNextPage}
        data={tableData}
        onFullyScroll={fetchNextPage}
      />
    </TopbarPage>
  );
};

export default Merch;
